/**
 * Theme management: Light / Dark / System.
 *
 * Persists the user's choice to localStorage under 'displaycms-theme'.
 * On load, reads the stored preference and applies it to
 * document.documentElement.dataset.theme; index.css's [data-theme="dark"]
 * selector then activates the dark token set.
 *
 * "System" mode observes prefers-color-scheme and updates the attribute
 * reactively when the OS preference changes.
 */

export type Theme = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'displaycms-theme';

let systemMediaQuery: MediaQueryList | null = null;
let systemListener: (() => void) | null = null;

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;

  // Tear down previous system listener if any.
  if (systemListener && systemMediaQuery) {
    systemMediaQuery.removeEventListener('change', systemListener);
    systemListener = null;
    systemMediaQuery = null;
  }

  if (theme === 'dark') {
    document.documentElement.dataset['theme'] = 'dark';
  } else if (theme === 'light') {
    delete document.documentElement.dataset['theme'];
  } else {
    // System: set immediately and listen for changes.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    systemMediaQuery = mq;
    const apply = (): void => {
      if (mq.matches) {
        document.documentElement.dataset['theme'] = 'dark';
      } else {
        delete document.documentElement.dataset['theme'];
      }
    };
    apply();
    systemListener = apply;
    mq.addEventListener('change', systemListener);
  }
}

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // ignore
  }
  return 'system';
}

export function persistTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // best-effort
  }
  applyTheme(theme);
}

/**
 * Called once at app bootstrap (main.tsx / App.tsx) to restore the
 * persisted theme preference before React renders.
 */
export function initTheme(): void {
  applyTheme(getStoredTheme());
}
